import { AdjustCrop, AdjustCropRect, AdjustResize, CROP_ASPECTS } from './adjust.types'

/**
 * Geometry of the Adjust editor. Three spaces:
 *  - IMAGE: source px of the unrotated photo, origin top-left (blurs, shapes and stickers live here)
 *  - FRAME: the photo flipped, then rotated about its centre; origin = the photo's centre (the crop frame lives here)
 *  - SCREEN: CSS px of the canvas
 * jsdom has no DOMMatrix, so this file carries its own 2D affine matrix (canvas `setTransform` order: a b c d e f).
 */

export interface Size {
  width: number
  height: number
}

export interface Point {
  x: number
  y: number
}

export interface Matrix {
  a: number
  b: number
  c: number
  d: number
  e: number
  f: number
}

export const IDENTITY: Matrix = { a: 1, b: 0, c: 0, d: 1, e: 0, f: 0 }

/** m × n: applies n first, then m */
export const multiply = (m: Matrix, n: Matrix): Matrix => ({
  a: m.a * n.a + m.c * n.b,
  b: m.b * n.a + m.d * n.b,
  c: m.a * n.c + m.c * n.d,
  d: m.b * n.c + m.d * n.d,
  e: m.a * n.e + m.c * n.f + m.e,
  f: m.b * n.e + m.d * n.f + m.f,
})

export const compose = (...matrices: Matrix[]): Matrix => matrices.reduce((acc, m) => multiply(acc, m), IDENTITY)

export const translation = (x: number, y: number): Matrix => ({ a: 1, b: 0, c: 0, d: 1, e: x, f: y })
export const scaling = (x: number, y: number = x): Matrix => ({ a: x, b: 0, c: 0, d: y, e: 0, f: 0 })
export const rotation = (degrees: number): Matrix => {
  const r = degrees * Math.PI / 180
  const cos = Math.cos(r)
  const sin = Math.sin(r)
  return { a: cos, b: sin, c: -sin, d: cos, e: 0, f: 0 }
}

export const invert = (m: Matrix): Matrix => {
  const det = m.a * m.d - m.b * m.c
  if (!det) return IDENTITY
  return {
    a: m.d / det,
    b: -m.b / det,
    c: -m.c / det,
    d: m.a / det,
    e: (m.c * m.f - m.d * m.e) / det,
    f: (m.b * m.e - m.a * m.f) / det,
  }
}

export const apply = (m: Matrix, p: Point): Point => ({ x: m.a * p.x + m.c * p.y + m.e, y: m.b * p.x + m.d * p.y + m.f })

/** Total clockwise rotation in degrees: quarter turns + straighten */
export const totalRotation = (crop: AdjustCrop): number => (((crop.quarterTurns % 4) + 4) % 4) * 90 + crop.straighten

const isQuarterOdd = (crop: AdjustCrop) => ((((crop.quarterTurns % 4) + 4) % 4) % 2) === 1

/** IMAGE → FRAME */
export const imageToFrame = (crop: AdjustCrop, image: Size): Matrix => compose(
  rotation(totalRotation(crop)),
  scaling(crop.flipX ? -1 : 1, crop.flipY ? -1 : 1),
  translation(-image.width / 2, -image.height / 2)
)

/** The ratio a frame keeps (width / height), or null when free (Custom with a dragged frame) */
export const frameRatio = (crop: AdjustCrop, image: Size): number | null => {
  const preset = CROP_ASPECTS.find(aspect => aspect.id === crop.aspect)
  if (preset?.ratio) {
    const ratio = preset.ratio === 1 ? 1 : crop.portrait ? 1 / preset.ratio : preset.ratio
    return ratio
  }
  if (crop.aspect === 'custom' && crop.rect) return null
  // Original / Custom: the photo's own ratio, in its turned orientation
  return isQuarterOdd(crop) ? image.height / image.width : image.width / image.height
}

/** Largest centred frame of `ratio` inside the photo rotated by `degrees` — never shows a blank corner */
export const largestFrame = (ratio: number, degrees: number, image: Size): Size => {
  const r = degrees * Math.PI / 180
  const cos = Math.abs(Math.cos(r))
  const sin = Math.abs(Math.sin(r))
  const width = Math.min(image.width / (cos + sin / ratio), image.height / (sin + cos / ratio))
  return { width, height: width / ratio }
}

/** The crop frame in FRAME space */
export const frameRectOf = (crop: AdjustCrop, image: Size): AdjustCropRect => {
  if (crop.rect) return crop.rect
  const ratio = frameRatio(crop, image) ?? image.width / image.height
  const size = largestFrame(ratio, totalRotation(crop), image)
  return { cx: 0, cy: 0, width: size.width, height: size.height }
}

const EPSILON = 0.5

/** Whether every corner of `rect` (FRAME space) falls on the photo */
export const isRectInside = (rect: AdjustCropRect, degrees: number, image: Size): boolean => {
  // Flips mirror the photo onto itself, so only the rotation matters
  const back = rotation(-degrees)
  const halfW = image.width / 2 + EPSILON
  const halfH = image.height / 2 + EPSILON
  return [[-1, -1], [1, -1], [1, 1], [-1, 1]].every(([sx, sy]) => {
    const p = apply(back, { x: rect.cx + sx * rect.width / 2, y: rect.cy + sy * rect.height / 2 })
    return Math.abs(p.x) <= halfW && Math.abs(p.y) <= halfH
  })
}

const lerpRect = (from: AdjustCropRect, to: AdjustCropRect, t: number): AdjustCropRect => ({
  cx: from.cx + (to.cx - from.cx) * t,
  cy: from.cy + (to.cy - from.cy) * t,
  width: from.width + (to.width - from.width) * t,
  height: from.height + (to.height - from.height) * t,
})

/** The furthest point from a valid `from` towards `to` that stays on the photo (bisection) */
export const constrainTowards = (from: AdjustCropRect, to: AdjustCropRect, degrees: number, image: Size): AdjustCropRect => {
  if (isRectInside(to, degrees, image)) return to
  let lo = 0
  let hi = 1
  for (let i = 0; i < 18; i++) {
    const mid = (lo + hi) / 2
    if (isRectInside(lerpRect(from, to, mid), degrees, image)) lo = mid
    else hi = mid
  }
  return lerpRect(from, to, lo)
}

/** After a rotation change: keep a dragged frame by shrinking it about its centre, or fall back to the largest one */
export const refitRect = (rect: AdjustCropRect | null, degrees: number, image: Size): AdjustCropRect | null => {
  if (!rect || isRectInside(rect, degrees, image)) return rect
  const tiny = { ...rect, width: rect.width * 0.02, height: rect.height * 0.02 }
  if (!isRectInside(tiny, degrees, image)) return null
  return constrainTowards(tiny, rect, degrees, image)
}

export type CropHandle = 'move' | 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw'

/** Handle under a FRAME-space point; `tolerance` in FRAME units */
export const hitCropHandle = (p: Point, rect: AdjustCropRect, tolerance: number): CropHandle | null => {
  const left = rect.cx - rect.width / 2
  const right = rect.cx + rect.width / 2
  const top = rect.cy - rect.height / 2
  const bottom = rect.cy + rect.height / 2
  const nearL = Math.abs(p.x - left) <= tolerance
  const nearR = Math.abs(p.x - right) <= tolerance
  const nearT = Math.abs(p.y - top) <= tolerance
  const nearB = Math.abs(p.y - bottom) <= tolerance
  const inX = p.x >= left - tolerance && p.x <= right + tolerance
  const inY = p.y >= top - tolerance && p.y <= bottom + tolerance
  if (nearT && nearL) return 'nw'
  if (nearT && nearR) return 'ne'
  if (nearB && nearL) return 'sw'
  if (nearB && nearR) return 'se'
  if (nearT && inX) return 'n'
  if (nearB && inX) return 's'
  if (nearL && inY) return 'w'
  if (nearR && inY) return 'e'
  if (p.x > left && p.x < right && p.y > top && p.y < bottom) return 'move'
  return null
}

/**
 * Drag a crop handle by `delta` (FRAME units). `ratio` locks the frame's proportions (null = free). The result never
 * leaves the photo and never gets smaller than `minSize`.
 */
export const dragCropRect = (
  start: AdjustCropRect,
  handle: CropHandle,
  delta: Point,
  ratio: number | null,
  degrees: number,
  image: Size,
  minSize: number
): AdjustCropRect => {
  if (handle === 'move') {
    return constrainTowards(start, { ...start, cx: start.cx + delta.x, cy: start.cy + delta.y }, degrees, image)
  }

  let left = start.cx - start.width / 2
  let right = start.cx + start.width / 2
  let top = start.cy - start.height / 2
  let bottom = start.cy + start.height / 2
  if (handle.includes('w')) left = Math.min(left + delta.x, right - minSize)
  if (handle.includes('e')) right = Math.max(right + delta.x, left + minSize)
  if (handle.includes('n')) top = Math.min(top + delta.y, bottom - minSize)
  if (handle.includes('s')) bottom = Math.max(bottom + delta.y, top + minSize)

  let width = right - left
  let height = bottom - top
  if (ratio) {
    if (handle === 'n' || handle === 's') width = height * ratio
    else if (handle === 'e' || handle === 'w') height = width / ratio
    else if (width / start.width > height / start.height) height = width / ratio
    else width = height * ratio
    // Anchor the side opposite the handle; an edge handle keeps the frame centred on the other axis
    if (handle.includes('w')) left = right - width
    else if (handle.includes('e')) right = left + width
    else { left = start.cx - width / 2; right = start.cx + width / 2 }
    if (handle.includes('n')) top = bottom - height
    else if (handle.includes('s')) bottom = top + height
    else { top = start.cy - height / 2; bottom = start.cy + height / 2 }
  }

  const target = { cx: (left + right) / 2, cy: (top + bottom) / 2, width: right - left, height: bottom - top }
  const from = isRectInside(start, degrees, image) ? start : refitRect(start, degrees, image) ?? start
  return constrainTowards(from, target, degrees, image)
}

/** Bounding box of the rotated photo (crop view shows all of it) */
export const rotatedBounds = (degrees: number, image: Size): Size => {
  const r = degrees * Math.PI / 180
  const cos = Math.abs(Math.cos(r))
  const sin = Math.abs(Math.sin(r))
  return { width: image.width * cos + image.height * sin, height: image.width * sin + image.height * cos }
}

export interface ViewParams {
  crop: AdjustCrop
  image: Size
  viewport: Size
  /** 1 = fit */
  zoom: number
  pan: Point
  /** The crop tool shows the whole rotated photo; every other tool shows the cropped result */
  showWholePhoto: boolean
  padding: number
}

export interface ViewTransform {
  /** IMAGE → SCREEN */
  imageToScreen: Matrix
  /** FRAME → SCREEN */
  frameToScreen: Matrix
  /** FRAME units → screen px */
  scale: number
  frame: AdjustCropRect
}

export const viewTransform = ({ crop, image, viewport, zoom, pan, showWholePhoto, padding }: ViewParams): ViewTransform => {
  const degrees = totalRotation(crop)
  const frame = frameRectOf(crop, image)
  const fit = showWholePhoto ? rotatedBounds(degrees, image) : { width: frame.width, height: frame.height }
  const focus = showWholePhoto ? { x: 0, y: 0 } : { x: frame.cx, y: frame.cy }
  const available = { width: Math.max(1, viewport.width - 2 * padding), height: Math.max(1, viewport.height - 2 * padding) }
  const scale = Math.min(available.width / Math.max(1, fit.width), available.height / Math.max(1, fit.height)) * zoom
  const frameToScreen = compose(
    translation(viewport.width / 2 + pan.x, viewport.height / 2 + pan.y),
    scaling(scale),
    translation(-focus.x, -focus.y)
  )
  return { imageToScreen: multiply(frameToScreen, imageToFrame(crop, image)), frameToScreen, scale, frame }
}

/** Keep the point under the cursor fixed while zooming */
export const zoomAbout = (cursor: Point, viewport: Size, pan: Point, zoom: number, nextZoom: number): Point => {
  const cx = viewport.width / 2
  const cy = viewport.height / 2
  const ratio = nextZoom / zoom
  return { x: cursor.x - cx - (cursor.x - cx - pan.x) * ratio, y: cursor.y - cy - (cursor.y - cy - pan.y) * ratio }
}

/** Saved size after the crop and the resize; never larger than the crop */
export const outputSize = (crop: AdjustCrop, resize: AdjustResize, image: Size): Size => {
  const frame = frameRectOf(crop, image)
  const frameWidth = Math.max(1, Math.round(frame.width))
  const frameHeight = Math.max(1, Math.round(frame.height))
  const width = Math.max(1, Math.min(frameWidth, Math.round(frame.width * resize.scale)))
  const height = resize.lock || resize.height === null
    ? Math.max(1, Math.min(frameHeight, Math.round(width * frame.height / frame.width)))
    : Math.max(1, Math.min(frameHeight, Math.round(resize.height)))
  return { width, height }
}
